import { Box, IconButton, Typography, useTheme, Drawer, TextField, Tooltip } from '@mui/material';
import { ColumnDef } from '@tanstack/react-table';
import {
  useDeleteChunk,
  useGetChunks,
  useGetDataSet,
  useCreateOrUpdateDataSet
} from 'apis/business/setting/knowledgeBase/hook';
import { ChunkResponse } from 'apis/business/setting/knowledgeBase/types';
import DataTable from 'components/Common/DataTable';
import Pagination, { usePagination } from 'components/Common/Pagination';
import Search from 'components/Common/Search';
import { useEffect, useState } from 'react';
import { MdInfoOutline, MdOutlineEdit } from 'react-icons/md';
import { RiDeleteBin6Line } from 'react-icons/ri';
import { IoClose, IoCheckmark } from 'react-icons/io5';
import { Link, Outlet, useNavigate, useParams } from 'react-router-dom';
import { getSourceMeta, SOURCE_BADGE_SX } from '../../Agent/Configure/Knowledge/components/QuestionAnswer';

export default function Chunks() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { datasetId } = useParams();

  const [searchText, setSearchText] = useState<null | string>(null);
  const { currentPage, perPage, setPage, setPerPage } = usePagination();

  const [isEditingName, setIsEditingName] = useState(false);
  const [tempName, setTempName] = useState('');

  const { data: dataset } = useGetDataSet();
  const isWebSource = dataset?.datasource_type === 'website';
  const { data: chunks, isLoading } = useGetChunks(dataset?.id, { page: currentPage, per_page: perPage });
  const { mutateAsync: deleteChunk } = useDeleteChunk(String(datasetId));
  const { mutateAsync: updateDataset } = useCreateOrUpdateDataSet(true);

  useEffect(() => {
    if (dataset?.name) {
      setTempName(dataset.name);
    }
  }, [dataset]);

  const handleSaveName = async () => {
    if (!tempName?.trim()) return;
    await updateDataset({ ...dataset, name: tempName });
    setIsEditingName(false);
  };

  const handleCancelEdit = () => {
    setTempName(dataset?.name || '');
    setIsEditingName(false);
  };

  return (
    <Drawer
      anchor='right'
      open
      onClose={() => navigate(-1)}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 750, md: 1200 },
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2,
          borderBottom: '1px solid #DEDEDE',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Box display='flex' gap={1} alignItems='center' width='100%'>
          <Box display='flex' alignItems='center' gap={1} flex={1}>
            {isEditingName ? (
              <>
                <TextField
                  value={tempName}
                  onChange={(e: any) => setTempName(e.target.value)}
                  size='small'
                  autoFocus
                  sx={{ width: 350 }}
                  onKeyDown={(e: any) => e.key === 'Enter' && handleSaveName()}
                />

                <IconButton size='small' onClick={handleSaveName}>
                  <IoCheckmark size={20} />
                </IconButton>

                <IconButton size='small' onClick={handleCancelEdit}>
                  <IoClose size={20} />
                </IconButton>
              </>
            ) : (
              <>
                <Typography variant='h6'>{dataset?.name}</Typography>

                <IconButton size='small' onClick={() => setIsEditingName(true)}>
                  <MdOutlineEdit size={18} />
                </IconButton>
              </>
            )}
          </Box>
        </Box>

        <IconButton onClick={() => navigate(-1)}>
          <IoClose size={22} />
        </IconButton>
      </Box>
      <Box
        px={1.5}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            py: 2,
            px: 2,
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2
          }}
        >
          <Search
            size='small'
            fullWidth
            sx={{ background: theme.palette.background.paper }}
            placeholder='Search your file'
            searchText={searchText}
            setSearchText={setSearchText}
          />
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto', px: 2 }}>
          <DataTable
            data={chunks?.data || []}
            columns={isWebSource ? qaColumns(deleteChunk) : chunkColumns(deleteChunk)}
            isHeaderSticky
            isLoading={isLoading}
          />
        </Box>

        <Box p={2}>
          <Pagination
            totalPages={chunks?.meta?.total_pages}
            totalCount={chunks?.meta?.total_count ?? 0}
            currentPage={currentPage}
            perPage={perPage}
            onChange={setPage}
            onPerPageChange={setPerPage}
          />
        </Box>
      </Box>

      <Outlet />
    </Drawer>
  );
}

type DeleteFn = (id: string) => void;

const chunkColumns = (deleteChunk: DeleteFn): ColumnDef<ChunkResponse>[] => [
  {
    header: () => (
      <Box display='flex' flexDirection='row' gap={0.5} alignItems='center'>
        <Typography>Chunks</Typography>
        <Tooltip
          title='Note: AI automatically groups related questions and answers form uploaded PDFs into editable knowledge chunks for the voice agent'
          placement='top'
          arrow
          slotProps={{
            tooltip: {
              sx: {
                bgcolor: 'black',
                color: 'white',
                boxShadow: 3,
                fontSize: 12,
                borderRadius: 1,
                p: 1,
                lineHeight: 1.2
              }
            },
            arrow: {
              sx: { color: 'black' }
            }
          }}
        >
          <Box>
            <MdInfoOutline size={16} style={{ cursor: 'pointer', marginTop: '4px' }} />
          </Box>
        </Tooltip>
      </Box>
    ),
    accessorKey: 'text',
    cell: (info) => (
      <Typography fontSize={16} sx={{ wordBreak: 'break-word' }}>
        {info.getValue() as string}
      </Typography>
    )
  },
  {
    header: 'Action',
    cell: (info) => (
      <Box display='flex' gap={1}>
        <Link to={`${info.row.original.id}/edit`}>
          <IconButton>
            <MdOutlineEdit />
          </IconButton>
        </Link>

        <IconButton onClick={() => deleteChunk(info.row.original.id)} color='error'>
          <RiDeleteBin6Line />
        </IconButton>
      </Box>
    )
  }
];

const qaColumns = (deleteChunk: DeleteFn): ColumnDef<ChunkResponse>[] => [
  {
    header: 'Question',
    accessorKey: 'question',
    size: 250,
    cell: (info) => (
      <Typography fontSize={16} sx={{ wordBreak: 'break-word' }}>
        {info.getValue() as string}
      </Typography>
    )
  },
  {
    header: 'Answer',
    accessorKey: 'answer',
    size: 250,
    cell: (info) => (
      <Typography fontSize={16} sx={{ wordBreak: 'break-word' }}>
        {info.getValue() as string}
      </Typography>
    )
  },
  {
    header: 'Source',
    cell: (info) => {
      const rowData = info.row.original as any;
      const { label, key } = getSourceMeta(rowData?.metadata?.source);
      return (
        <Box
          sx={{
            width: 'fit-content',
            px: 1.6,
            py: 0.35,
            borderRadius: 999,
            fontSize: 13,
            fontWeight: 500,
            lineHeight: 1.4,
            ...SOURCE_BADGE_SX[key]
          }}
        >
          {label}
        </Box>
      );
    }
  },
  {
    header: 'Action',
    size: 50,
    cell: (info) => (
      <Box display='flex' gap={1}>
        <Link to={`${info.row.original.id}/edit`}>
          <IconButton>
            <MdOutlineEdit />
          </IconButton>
        </Link>

        <IconButton onClick={() => deleteChunk(info.row.original.id)} color='error'>
          <RiDeleteBin6Line />
        </IconButton>
      </Box>
    )
  }
];
